import React from 'react';
import { useTranslation } from 'react-i18next';
import { commonConversions } from '../data/mock';
import { getUnitById as getLengthUnitById } from '../data/lengthRoutes';
import { getUnitById as getWeightUnitById } from '../data/weightRoutes';
import { getUnitById as getTemperatureUnitById } from '../data/temperatureRoutes';
import { getUnitById as getAreaUnitById } from '../data/areaRoutes';
import { getUnitById as getVolumeUnitById } from '../data/volumeRoutes';
import LocalizedLink from './LocalizedLink';

// Map display names to unit IDs for length
const lengthUnitMap = {
  'cm': 'centimeter',
  'inches': 'inch',
  'mm': 'millimeter',
  'meters': 'meter',
  'feet': 'foot',
  'km': 'kilometer',
  'miles': 'mile',
  'yards': 'yard',
};

// Map display names to unit IDs for weight
const weightUnitMap = {
  'kg': 'kilogram',
  'lbs': 'pound',
  'pounds': 'pound',
  'grams': 'gram',
  'ounces': 'ounce',
  'stones': 'stone',
};

// Map display names to unit IDs for temperature
const temperatureUnitMap = {
  'celsius': 'celsius',
  'fahrenheit': 'fahrenheit',
  'kelvin': 'kelvin',
};

// Map display names to unit IDs for area
const areaUnitMap = {
  'acres': 'acre',
  'square feet': 'sqfoot',
  'sqft': 'sqfoot',
  'sq ft': 'sqfoot',
  'hectares': 'hectare',
  'square meters': 'sqmeter',
};

// Map display names to unit IDs for volume
const volumeUnitMap = {
  'liters': 'liter',
  'gallons': 'gallon-us',
  'ml': 'milliliter',
  'cups': 'cup-us',
};

const CommonConversions = ({ onSelectConversion }) => {
  const { t } = useTranslation();

  // Get translated unit name
  const getUnitName = (unitKey) => {
    const translationKey = `units.${unitKey.toLowerCase().replace(/\s+/g, '')}`;
    const translated = t(translationKey, { defaultValue: '' });
    return translated || unitKey;
  };

  // Split conversions into two columns
  const halfIndex = Math.ceil(commonConversions.length / 2);
  const leftColumn = commonConversions.slice(0, halfIndex);
  const rightColumn = commonConversions.slice(halfIndex);

  const ConversionLink = ({ conversion }) => {
    // Check if this is a length conversion that should link to the dedicated page
    if (conversion.category === 'length') {
      const fromId = lengthUnitMap[conversion.from.toLowerCase()] || conversion.from.toLowerCase();
      const toId = lengthUnitMap[conversion.to.toLowerCase()] || conversion.to.toLowerCase();

      // Verify both units exist in our length units
      if (getLengthUnitById(fromId) && getLengthUnitById(toId)) {
        return (
          <LocalizedLink
            to={`/convert/length/${fromId}-to-${toId}`}
            className="text-[#2563eb] hover:text-[#1d4ed8] hover:underline text-sm transition-colors"
          >
            {t('common.xToY', { from: getUnitName(conversion.from), to: getUnitName(conversion.to) })}
          </LocalizedLink>
        );
      }
    }

    // Check if this is a weight conversion that should link to the dedicated page
    if (conversion.category === 'weight') {
      const fromId = weightUnitMap[conversion.from.toLowerCase()] || conversion.from.toLowerCase();
      const toId = weightUnitMap[conversion.to.toLowerCase()] || conversion.to.toLowerCase();

      // Verify both units exist in our weight units
      if (getWeightUnitById(fromId) && getWeightUnitById(toId)) {
        return (
          <LocalizedLink
            to={`/convert/weight/${fromId}-to-${toId}`}
            className="text-[#2563eb] hover:text-[#1d4ed8] hover:underline text-sm transition-colors"
          >
            {t('common.xToY', { from: getUnitName(conversion.from), to: getUnitName(conversion.to) })}
          </LocalizedLink>
        );
      }
    }

    // Check if this is a temperature conversion that should link to the dedicated page
    if (conversion.category === 'temperature') {
      const fromId = temperatureUnitMap[conversion.from.toLowerCase()] || conversion.from.toLowerCase();
      const toId = temperatureUnitMap[conversion.to.toLowerCase()] || conversion.to.toLowerCase();

      // Verify both units exist in our temperature units
      if (getTemperatureUnitById(fromId) && getTemperatureUnitById(toId)) {
        return (
          <LocalizedLink
            to={`/convert/temperature/${fromId}-to-${toId}`}
            className="text-[#2563eb] hover:text-[#1d4ed8] hover:underline text-sm transition-colors"
          >
            {t('common.xToY', { from: getUnitName(conversion.from), to: getUnitName(conversion.to) })}
          </LocalizedLink>
        );
      }
    }

    // Check if this is an area conversion that should link to the dedicated page
    if (conversion.category === 'area') {
      const fromId = areaUnitMap[conversion.from.toLowerCase()] || conversion.from.toLowerCase();
      const toId = areaUnitMap[conversion.to.toLowerCase()] || conversion.to.toLowerCase();

      // Verify both units exist in our area units
      if (getAreaUnitById(fromId) && getAreaUnitById(toId)) {
        return (
          <LocalizedLink
            to={`/convert/area/${fromId}-to-${toId}`}
            className="text-[#2563eb] hover:text-[#1d4ed8] hover:underline text-sm transition-colors"
          >
            {t('common.xToY', { from: getUnitName(conversion.from), to: getUnitName(conversion.to) })}
          </LocalizedLink>
        );
      }
    }

    // Check if this is a volume conversion that should link to the dedicated page
    if (conversion.category === 'volume') {
      const fromId = volumeUnitMap[conversion.from.toLowerCase()] || conversion.from.toLowerCase();
      const toId = volumeUnitMap[conversion.to.toLowerCase()] || conversion.to.toLowerCase();

      // Verify both units exist in our volume units
      if (getVolumeUnitById(fromId) && getVolumeUnitById(toId)) {
        return (
          <LocalizedLink
            to={`/convert/volume/${fromId}-to-${toId}`}
            className="text-[#2563eb] hover:text-[#1d4ed8] hover:underline text-sm transition-colors"
          >
            {t('common.xToY', { from: getUnitName(conversion.from), to: getUnitName(conversion.to) })}
          </LocalizedLink>
        );
      }
    }

    // Default behavior for other conversions
    return (
      <button
        onClick={() => onSelectConversion(conversion)}
        className="text-[#2563eb] hover:text-[#1d4ed8] hover:underline text-sm transition-colors text-left"
      >
        {t('common.xToY', { from: getUnitName(conversion.from), to: getUnitName(conversion.to) })}
      </button>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">{t('common.commonConversions')}</h3>

      <div className="grid md:grid-cols-2 gap-x-8 gap-y-1">
        <div className="space-y-1">
          {leftColumn.map((conv, idx) => (
            <div key={`left-${idx}`} className="flex items-center gap-1">
              <span className="text-gray-400 text-xs">•</span>
              <ConversionLink conversion={conv} />
            </div>
          ))}
        </div>
        <div className="space-y-1">
          {rightColumn.map((conv, idx) => (
            <div key={`right-${idx}`} className="flex items-center gap-1">
              <span className="text-gray-400 text-xs">•</span>
              <ConversionLink conversion={conv} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CommonConversions;
